import { ITEMS, type ItemId } from '@/data/items';
import { SHOP_STOCK, buyPrice, isSellable, sellValue } from '@/data/shop';
import type { Inventory } from '@/world/inventory';
import type { Wallet } from '@/world/wallet';

/**
 * The travelling merchant's cart.
 *
 * Two columns rather than two modes: what you can buy, and what you are
 * carrying that is worth something. A shop with a Buy tab and a Sell tab makes
 * you leave one to check the other, and the only question worth answering at a
 * cart is "can I afford that, and what would cover it".
 *
 * Sells one at a time. Bulk selling is a spreadsheet, and a stack of forty
 * turnips is not a decision that needs a slider.
 */

export interface ShopPanelOptions {
  readonly container: HTMLElement;
  readonly inventory: Inventory;
  readonly wallet: Wallet;
  /** Fraction off, earned by knowing the merchant. */
  readonly discount: () => number;
  readonly onChanged: () => void;
  readonly onMessage: (message: string) => void;
}

export class ShopPanel {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly purse: HTMLSpanElement;

  constructor(private readonly options: ShopPanelOptions) {
    this.root = document.createElement('div');
    this.root.className = 'shop-root';

    this.panel = document.createElement('div');
    this.panel.className = 'shop-panel';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', 'Travelling merchant');

    const header = document.createElement('div');
    header.className = 'shop-header';

    const title = document.createElement('span');
    title.className = 'shop-title';
    title.textContent = 'Travelling Merchant';

    this.purse = document.createElement('span');
    this.purse.className = 'shop-purse';

    header.append(title, this.purse);

    this.body = document.createElement('div');
    this.body.className = 'shop-body';

    const done = document.createElement('button');
    done.className = 'gear-done';
    done.type = 'button';
    done.textContent = 'Done';
    done.addEventListener('click', () => {
      this.setOpen(false);
    });

    this.panel.append(header, this.body, done);
    this.root.appendChild(this.panel);
    options.container.appendChild(this.root);
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  setOpen(open: boolean): void {
    this.panel.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    if (open) this.refresh();
  }

  destroy(): void {
    this.root.remove();
  }

  private refresh(): void {
    const { inventory, wallet } = this.options;

    const off = Math.round(this.options.discount() * 100);
    this.purse.textContent = off > 0 ? `${wallet.coins} coins — ${off}% off` : `${wallet.coins} coins`;
    this.body.replaceChildren();

    this.body.appendChild(this.column('For sale', SHOP_STOCK.map((item) => this.buyRow(item))));

    // What you are carrying that is worth anything, gathered by kind rather
    // than by slot: three slots of turnips is one thing to sell.
    const carried: ItemId[] = [];
    const seen = new Set<ItemId>();
    for (let i = 0; i < inventory.capacity; i++) {
      const slot = inventory.slotAt(i);
      if (!slot || seen.has(slot.id) || !isSellable(slot.id)) continue;
      seen.add(slot.id);
      carried.push(slot.id);
    }

    this.body.appendChild(
      this.column(
        carried.length > 0 ? 'Your goods' : 'Nothing to sell',
        carried.map((item) => this.sellRow(item)),
      ),
    );
  }

  private column(title: string, rows: HTMLElement[]): HTMLElement {
    const column = document.createElement('div');
    column.className = 'shop-column';

    const heading = document.createElement('p');
    heading.className = 'gear-hint';
    heading.textContent = title;

    column.append(heading, ...rows);
    return column;
  }

  private buyRow(item: ItemId): HTMLElement {
    const { inventory, wallet, onChanged, onMessage } = this.options;
    const price = buyPrice(item, this.options.discount());

    const button = document.createElement('button');
    button.className = 'shop-row';
    button.type = 'button';
    button.disabled = !wallet.canAfford(price);
    button.style.setProperty('--tint', ITEMS[item].tint);

    const name = document.createElement('span');
    name.textContent = ITEMS[item].name;

    const cost = document.createElement('span');
    cost.className = 'shop-price';
    cost.textContent = `${price}c`;

    button.append(name, cost);
    button.addEventListener('click', () => {
      if (!wallet.canAfford(price)) return;
      // Room is checked before the coins move, so a full backpack cannot
      // charge you for something you never received.
      if (!inventory.hasRoomFor(item, 1)) {
        onMessage('No room for that');
        return;
      }
      wallet.spend(price);
      inventory.add(item, 1);
      onMessage(`Bought ${ITEMS[item].name}`);
      onChanged();
      this.refresh();
    });

    return button;
  }

  private sellRow(item: ItemId): HTMLElement {
    const { inventory, wallet, onChanged, onMessage } = this.options;
    const value = sellValue(item);
    const held = inventory.countOf(item);

    const button = document.createElement('button');
    button.className = 'shop-row';
    button.type = 'button';
    button.style.setProperty('--tint', ITEMS[item].tint);

    const name = document.createElement('span');
    name.textContent = `${ITEMS[item].name} ×${held}`;

    const price = document.createElement('span');
    price.className = 'shop-price';
    price.textContent = `+${value}c`;

    button.append(name, price);
    button.addEventListener('click', () => {
      if (inventory.remove(item, 1) !== 1) return;
      wallet.earn(value);
      onMessage(`Sold ${ITEMS[item].name} for ${value}c`);
      onChanged();
      this.refresh();
    });

    return button;
  }
}
